import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { $ } from '../utils/dom';
import { getUTCDateComponents } from '../utils/date-utils';


interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface AttendanceStats {
  weeklyCount: number;
  monthlyCount: number;
  allTimeCount: number;
}

interface AttendanceRecord {
  id: string;
  date: string;
  studentId: string;
}

import { getTyrisIcon } from '../components/ui/tyris-icon';

export class AdminAttendancePage implements View {
  private students: Student[] = [];
  private selectedStudentId = '';
  private stats: AttendanceStats | null = null;
  private allRecords: AttendanceRecord[] = [];
  private loading = false;

  async render(): Promise<string> {
    const params = router.getParams();
    if (params.studentId && params.studentId !== this.selectedStudentId) {
      this.selectedStudentId = params.studentId;
    }

    if (this.students.length === 0) {
      await this.loadStudents();
    }

    if (this.selectedStudentId && !this.stats && !this.loading) {
      await this.loadStudentData();
    }

    const selectedStudent = this.students.find((s) => s.id === this.selectedStudentId);

    const breadcrumbHtml = selectedStudent
      ? `<nav class="admin-breadcrumb">
          <a href="#/admin/students">Alumnos</a>
          <span class="admin-breadcrumb-sep">›</span>
          <a href="#/admin/student?id=${selectedStudent.id}">${selectedStudent.firstName} ${selectedStudent.lastName}</a>
          <span class="admin-breadcrumb-sep">›</span>
          <span>Asistencia</span>
        </nav>`
      : `<nav class="admin-breadcrumb">
          <a href="#/admin/students">Alumnos</a>
          <span class="admin-breadcrumb-sep">›</span>
          <span>Asistencia</span>
        </nav>`;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          ${breadcrumbHtml}
          <h2 data-testid="admin-attendance-title" style="display:flex;align-items:center;gap:0.6rem;">${getTyrisIcon('asistencia', { size: 28 })} Asistencia</h2>
          <p>Estadísticas de asistencia por alumno.</p>
        </div>
      </div>

      <section class="card-glass" data-testid="admin-attendance-selector">
        <h3 class="admin-section-title">Seleccionar alumno</h3>
        <div class="admin-inline-form">
          <select id="student-select" class="admin-select" data-testid="attendance-student-select">
            <option value="">— Selecciona un alumno —</option>
            ${this.students
              .map(
                (s) =>
                  `<option value="${s.id}" ${s.id === this.selectedStudentId ? 'selected' : ''}>${s.firstName} ${s.lastName} (${s.email})</option>`,
              )
              .join('')}
          </select>
          ${
            selectedStudent
              ? `<a href="#/admin/student?id=${selectedStudent.id}" class="btn btn-outline btn-sm">Ver ficha →</a>`
              : ''
          }
        </div>
      </section>

      ${
        !this.selectedStudentId
          ? `<div class="admin-empty" data-testid="admin-attendance-no-student"><p>Selecciona un alumno para ver su asistencia.</p></div>`
          : this.renderStudentAttendance(selectedStudent)
      }
    `;
  }

  private renderStudentAttendance(student: Student | undefined): string {
    if (!this.stats) {
      return `<div class="admin-state-loading"><p>Cargando asistencia...</p></div>`;
    }

    const calendarHtml = this.renderCalendar();

    return `
      ${student ? `<h3 class="admin-student-name" data-testid="admin-attendance-student-name">${student.firstName} ${student.lastName}</h3>` : ''}

      <div class="admin-kpi-grid" style="margin-bottom:1.5rem;" data-testid="admin-attendance-stats">
        <div class="kpi-card">
          <span class="kpi-value" data-testid="attendance-weekly">${this.stats.weeklyCount}</span>
          <span class="kpi-label">Esta semana</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-value" data-testid="attendance-monthly">${this.stats.monthlyCount}</span>
          <span class="kpi-label">Este mes</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-value" data-testid="attendance-alltime">${this.stats.allTimeCount}</span>
          <span class="kpi-label">Total histórico</span>
        </div>
      </div>

      ${calendarHtml}
    `;
  }

  private renderCalendar(): string {
    if (this.allRecords.length === 0) {
      return `
        <section class="card-glass" data-testid="admin-attendance-calendar">
          <h3 class="admin-section-title">Calendario de asistencias</h3>
          <div class="admin-empty"><p>Sin registros de asistencia disponibles.</p></div>
        </section>
      `;
    }

    // Group by year-month
    const byMonth: Record<string, string[]> = {};
    for (const r of this.allRecords) {
      const comps = getUTCDateComponents(r.date);
      const key = `${comps.year}-${String(comps.month).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = [];
      byMonth[key].push(r.date);
    }

    const months = Object.keys(byMonth).sort().reverse().slice(0, 3);

    const monthsHtml = months
      .map((monthKey) => {
        const [year, month] = monthKey.split('-').map(Number);
        const dates = new Set(byMonth[monthKey].map((d) => getUTCDateComponents(d).day));
        const firstDay = new Date(year, month - 1, 1).getDay();
        const daysInMonth = new Date(year, month, 0).getDate();
        const monthName = new Date(year, month - 1, 1).toLocaleDateString('es-ES', {
          month: 'long',
          year: 'numeric',
        });

        const cells: string[] = [];
        // Empty cells for first day offset (Mon=0)
        const offset = (firstDay + 6) % 7;
        for (let i = 0; i < offset; i++) cells.push(`<div></div>`);
        for (let d = 1; d <= daysInMonth; d++) {
          const attended = dates.has(d);
          cells.push(
            `<div class="cal-day ${attended ? 'cal-day-attended' : ''}" title="${attended ? 'Asistió' : ''}">${d}</div>`,
          );
        }

        return `
          <div class="cal-month">
            <h4 class="cal-month-title">${monthName}</h4>
            <div class="cal-grid">
              <div class="cal-header">L</div><div class="cal-header">M</div><div class="cal-header">X</div>
              <div class="cal-header">J</div><div class="cal-header">V</div><div class="cal-header">S</div><div class="cal-header">D</div>
              ${cells.join('')}
            </div>
          </div>
        `;
      })
      .join('');

    return `
      <section class="card-glass" data-testid="admin-attendance-calendar">
        <h3 class="admin-section-title">Calendario (últimos 3 meses)</h3>
        <div class="admin-calendar-months">
          ${monthsHtml}
        </div>
      </section>
    `;
  }

  afterRender(): void {
    const select = $('#student-select') as HTMLSelectElement | null;
    select?.addEventListener('change', () => {
      if (select.value) {
        window.location.hash = `#/admin/attendance?studentId=${select.value}`;
      } else {
        window.location.hash = '#/admin/attendance';
      }
    });
  }

  private async loadStudents(): Promise<void> {
    try {
      this.students = await apiClient.get<Student[]>('/api/users/students');
    } catch {
      toast.error('No se pudo cargar el listado de alumnos.');
    }
  }

  private async loadStudentData(): Promise<void> {
    this.loading = true;
    try {
      const [stats, history] = await Promise.all([
        apiClient.get<AttendanceStats>(
          `/api/attendance/student/${this.selectedStudentId}/stats`,
        ),
        apiClient.get<AttendanceRecord[]>(
          `/api/attendance/student/${this.selectedStudentId}/history`,
        ),
      ]);
      this.stats = stats;
      this.allRecords = history;
    } catch {
      this.stats = { weeklyCount: 0, monthlyCount: 0, allTimeCount: 0 };
      this.allRecords = [];
    } finally {
      this.loading = false;
    }
  }
}
